import React, {useState} from 'react';
import {Form, Formik, FormikErrors, setNestedObjectValues} from 'formik';
import Button from 'src/components/button';
import Checkbox from 'shared/components/checkbox';
import Label from 'shared/components/label';
import Text from 'shared/components/text';
import Notifications from 'shared/components/notifications';
import ShutDownModal from '../ShutDownModal';
import PhotoListings from '../PhotoListing';
import FormikCheckboxes from 'src/components/formik-checkboxes';
import {educationPhaseOptions} from 'src/constants';
import ColumnMappings from './ColumnMappings';
import ManageAllowedEmailDomains from './manage-allowed-email-domains/manage-allowed-email-domains';
import ScheduledReportFileNameMappings from './scheduled-report-file-name-mappings';
import ManageTestimonials from './manage-testimonials/manage-testimonials';
import {useDispatch} from 'react-redux';
import {saveUniversity} from 'src/redux/adminUniversity/actions/university';
import FormikRadioButtons from '../../../components/formik-radio-buttons';
import FormikTextInput from '../../../components/inputs/formik-text-input';
import FormikErrorText from '../../../components/formik-error-text';

type AddCollegeImage = $TSFixMe;
export type DeleteCollegeImage = $TSFixMe;

export interface UniversityData {
  _id: string;
  churnedAt?: string;
  slug?: string; // Note: This is not usually part of a university object
  joinPageSlug?: string; // Note: This is not usually part of a university object
  allowInternationalDownloads?: boolean;
  useApplyByTerminology?: boolean;
  enableIndividualSpotAwards?: boolean;
  primaryMarketSubdivisions?: string[]; // backend has them as arrays
  primaryMarketHighSchools?: string[];
  primaryMarketColleges?: string[];
  addCollegeImages?: AddCollegeImage[];
  deleteCollegeImages?: Set<DeleteCollegeImage>;
  useRanked?: boolean; // Note: this is not usually part of a university object
  educationPhasesDownloadable?: unknown;
  coreClientId?: string;
  collegeListingId?: string;
}

export interface UniversityFormValues {
  slug: string;
  joinPageSlug: string;
  allowInternationalDownloads: boolean;
  useApplyByTerminology?: boolean;
  enableIndividualSpotAwards: boolean;
  primaryMarketSubdivisions: string;
  primaryMarketHighSchools: string;
  primaryMarketColleges: string;
  addCollegeImages: AddCollegeImage[];
  deleteCollegeImages: Set<DeleteCollegeImage>;
  educationPhasesDownloadable: unknown;
  coreClientId: string;
  useRanked: boolean;
}

export interface Props {
  name: string;
  university: UniversityData;
}

const UniversityForm = ({name, university}: Props) => {
  const dispatch = useDispatch();
  const [showShutdownModal, setShowShutdownModal] = useState(false);

  const initialValues: UniversityFormValues = {
    slug: university.slug || '',
    joinPageSlug: university.joinPageSlug || '',
    allowInternationalDownloads: university.allowInternationalDownloads || true,
    useApplyByTerminology: university.useApplyByTerminology,
    enableIndividualSpotAwards: university.enableIndividualSpotAwards || false,
    primaryMarketSubdivisions: university.primaryMarketSubdivisions?.join(',') || '',
    primaryMarketHighSchools: university.primaryMarketHighSchools?.join(',') || '',
    primaryMarketColleges: university.primaryMarketColleges?.join(',') || '',
    addCollegeImages: university.addCollegeImages || [], // some of these might be wrong
    deleteCollegeImages: university.deleteCollegeImages || new Set(),
    educationPhasesDownloadable: university.educationPhasesDownloadable || [],
    coreClientId: university.coreClientId || '',
    useRanked: university.useRanked || false,
  };

  return (
    <div className="university-admin">
      {university._id && (
        <ShutDownModal
          name={name}
          show={showShutdownModal}
          onDismiss={() => setShowShutdownModal(false)}
          university={university}
        />
      )}
      <Formik
        initialValues={initialValues}
        onSubmit={async (values: UniversityFormValues, formikHelpers) => {
          try {
            await saveUniversity(values, university._id)(dispatch);
          } catch (e) {
            console.error(e);
            formikHelpers.setSubmitting(false);
            formikHelpers.setErrors(e as FormikErrors<UniversityFormValues>);
            formikHelpers.setTouched(setNestedObjectValues(e, true), false);
          }
        }}
      >
        {({values, setFieldValue, isSubmitting, isValid}) => (
          <Form>
            <Notifications />
            {!university._id && <Text>You are creating this partner</Text>}
            {university.churnedAt && <p>Partner churned on {university.churnedAt}</p>}
            <Checkbox
              label="Allow International Downloads?"
              checked={values.allowInternationalDownloads}
              onChange={() =>
                setFieldValue('allowInternationalDownloads', !values.allowInternationalDownloads)
              }
            />
            Enable this only if the partner is paying us for this service.
            <Checkbox
              label="Allow spot awarding individual students?"
              checked={values.enableIndividualSpotAwards}
              onChange={() =>
                setFieldValue('enableIndividualSpotAwards', !values.enableIndividualSpotAwards)
              }
            />
            <FormikCheckboxes
              name="educationPhasesDownloadable"
              label="Active Education Phases"
              options={educationPhaseOptions}
              required={false}
            />
            <FormikRadioButtons
              name="useApplyByTerminology"
              options={[
                {value: 'true', label: 'Apply By'},
                {
                  value: 'false',
                  label: 'Admitted By',
                },
              ]}
              legendText="Use 'Apply By' or 'Admitted by' terminology for relevant program labels"
            />
            <Label>
              Core-Client ID
              <FormikTextInput placeholder="identifier" name="coreClientId" />
            </Label>
            <Label>
              Join Page URL Slug (MANDATORY)
              <FormikTextInput placeholder="e.g. OSU" name="joinPageSlug" />
            </Label>
            <Label>
              Primary Market Target States (optional, comma-separated list, for partner dashboards)
              <FormikTextInput placeholder="e.g. CA,OR,WA,NV,AZ" name="primaryMarketSubdivisions" />
            </Label>
            <Label>
              Primary Market Target High Schools (optional, comma-separated list of CEEB or IPEDS
              IDs, for partner dashboards)
              <FormikTextInput
                placeholder="e.g. 123456,123457,123458"
                name="primaryMarketHighSchools"
              />
            </Label>
            <Label>
              Primary Market Target Colleges (optional, comma-separated list of IPEDS IDs, for
              partner dashboards)
              <FormikTextInput
                placeholder="e.g. 654321,654322,654323"
                name="primaryMarketColleges"
              />
            </Label>
            {university._id && <PhotoListings universityId={university._id} />}
            <FormikErrorText fieldName="college_listing_id" />
            <Button
              horizontalMargin={!!university._id}
              type="submit"
              disabled={isSubmitting || !isValid}
            >
              {isSubmitting ? 'Submitting...' : 'Submit'}
            </Button>
          </Form>
        )}
      </Formik>
      {university._id && <ColumnMappings universityId={university._id || ''} />}
      {university._id && <ManageAllowedEmailDomains universityId={university._id || ''} />}
      {university._id && <ScheduledReportFileNameMappings universityId={university._id || ''} />}
      {university._id && (
        <ManageTestimonials collegeListingId={university.collegeListingId || ''} />
      )}
      {university._id && (
        <Button
          color="negative"
          type="button"
          onClick={() => setShowShutdownModal(true)}
          disabled={!!university.churnedAt}
          className="mt-24"
        >
          Shut Down Partner
        </Button>
      )}
    </div>
  );
};

export default UniversityForm;
